<template>
    <b-modal ref="modalActions" lazy title-tag="h2" :title="this.title">
        <div v-if="type === 'PRINT'">
            <b-form-group label-cols="3" label="Message:" label-for="message">
                <b-form-textarea
                    v-model="message"
                    placeholder="Message (required)"
                    rows="3"
                    max-rows="6"
                    :maxlength="maxCharacters"
                    :disabled="isLoading"
                ></b-form-textarea>
                <p class="remaining-chars">
                    {{ remainingChars }} character(s) remaining
                </p>
            </b-form-group>
        </div>

        <p v-else-if="type === 'RESEND'">
            You are about to resend {{ resendMessage }}. Non-electronic
            reminder(s) will be ignored.
        </p>

        <div v-else-if="type === 'LOGCALL'">
            <p>
                You are about to create a communication record for
                {{ items.length }}. The optional note below will be included in
                the communication record.
            </p>
            <b-form-group label-for="notes" label-cols="3" label="Notes:">
                <b-textarea
                    placeholder="Notes (optional)"
                    v-model="notes"
                    :disabled="isLoading"
                />
            </b-form-group>
        </div>

        <template v-slot:modal-footer="{ cancel }">
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="cancel"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading"
                variant="primary"
                @click="submit"
                :disabled="disableSubmitButton"
            >
                {{ labelSubmit }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Loader from "@/module/commons/loader";
import { DateTime } from "luxon";

const PRINT = "PRINT";
const LOGCALL = "LOGCALL";
const RESEND = "RESEND";

export default {
    components: {
        Loader
    },
    data() {
        return {
            type: null,
            title: "",
            isLoading: false,
            labelSubmit: "",
            notes: "",
            message: "",
            maxCharacters: 200,
            items: [], // raw
            properItems: [] // array of object
        };
    },
    computed: {
        remainingChars() {
            return this.maxCharacters - this.message.length;
        },
        disableSubmitButton() {
            if (this.type === PRINT && this.message === "") {
                return true;
            }

            return false;
        },
        resendMessage() {
            let email = this.properItems.filter(i => i.typeId === 1).length;
            let sms = this.properItems.filter(i => i.typeId === 5).length;
            let total = email + sms; // electronic reminders only

            if (email > 0 && sms === 0) {
                return `${total} email reminder(s)`;
            }

            if (email === 0 && sms > 0) {
                return `${total} SMS reminder(s)`;
            }

            if (email > 0 && sms > 0) {
                return `${total} email and SMS reminder(s)`;
            }

            return `reminder(s)`;
        }
    },
    methods: {
        open(params) {
            let mapType = {
                PRINT: {
                    title: "Print Reminder Cards",
                    labelSubmit: "Print"
                },
                RESEND: {
                    title: "Resend Reminders",
                    labelSubmit: "Send"
                },
                LOGCALL: {
                    title: "Log Call",
                    labelSubmit: "Create"
                }
            };

            let detail = mapType[params.type];

            if (!detail) {
                return;
            }

            let { formDataItem, properItems } = this.generateProperItems(
                params.items
            );

            this.items = formDataItem;
            this.properItems = properItems;
            this.type = params.type;
            this.title = detail.title;
            this.labelSubmit = detail.labelSubmit;

            if (params.type === PRINT) {
                if (this._setting.postcard_settings_footer) {
                    this.message = this._setting.postcard_settings_footer.message;
                }
            }

            this.$refs.modalActions.show();
        },
        generateProperItems(items = []) {
            let result = [];
            let formDataItem = [];

            let typeMap = {
                1: "Email",
                2: "Phone",
                3: "Mail",
                4: "No Reminder",
                5: "SMS (Text)"
            };

            for (let item of items) {
                let [
                    reminderId,
                    reminderActorId,
                    typeId,
                    clientId,
                    patientId
                ] = item.split(",");
                typeId = Number(typeId);

                // format 1: object
                result.push({
                    typeId: typeId,
                    typeName: typeMap[typeId],
                    reminderId,
                    reminderActorId,
                    clientId,
                    patientId
                });

                // format 2: string (to be used on form data)
                // exlude clientId & patientId
                formDataItem.push(`${reminderId},${reminderActorId},${typeId}`);
            }

            return {
                formDataItem: formDataItem,
                properItems: result
            };
        },
        async submit() {
            // prettier-ignore
            let route = {
                PRINT: process.env.CI_APPS + "common/reminder/pdf",
                LOGCALL: process.env.BARRAMUNDI + "communication/storeBulk",
                RESEND: process.env.BARRAMUNDI + "schedule/reminder/bulkResend"
            };

            let uri = route[this.type];
            let data;
            let formData = new FormData();
            let logCallData = [];
            let resendData = [];
            let logDate = DateTime.local()
                .setZone(this._timeZone)
                .startOf("second") // <--As per the document { suppressMilliseconds: true } will only work if the value is 0
                .toISO({ suppressMilliseconds: true });
            let logContent = `Contacted client regarding reminder. Communication created from Reminders Detail report.`;

            // pre-submit: params preparation
            switch (this.type) {
                case PRINT:
                    for (let item of this.items) {
                        formData.append("ids[]", `'${item}'`);
                    }
                    formData.append("message", this.message);
                    data = formData;
                    break;

                case RESEND:
                    for (let item of this.properItems) {
                        resendData.push({
                            reminder_id: item.reminderId,
                            reminder_actor_id: item.reminderActorId,
                            reminder_type: item.typeId
                        });
                    }
                    data = resendData;
                    break;

                case LOGCALL:
                    if (this.notes !== "") {
                        logContent += ` ${this.notes}`;
                    }

                    for (let item of this.properItems) {
                        logCallData.push({
                            content: logContent,
                            patient_id: item.patientId || null,
                            client_id: item.clientId,
                            business_id: this._setting.business_id,
                            date: logDate,
                            type: 1, // <-- this is "Phone" type for commucation, not reminder
                            reference_id: item.reminderActorId,
                            reference_type: 11 // <-- this is "Reminder" type from TblCommunicationPeer
                        });
                    }

                    data = logCallData;
                    break;

                default:
                    break;
            }

            // submit
            this.isLoading = true;
            let res = await this.$http.post(uri, data);

            // post-submit
            // print
            if (this.type === PRINT) {
                if (res.data.uri) {
                    window.open(res.data.uri, "_blank");
                    this.$refs.modalActions.hide();
                } else if (res.data.is_background) {
                    let msg = res.data.message;
                    this.$refs.modalActions.hide();
                    this._message(msg, "warning");
                } else {
                    let msg = res.data.message || "Unable to generate PDF";
                    this.isLoading = false;
                    this._message(msg, "danger");
                }
            }
            // resend
            else if (this.type === RESEND) {
                // success
                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code === 200
                ) {
                    this._message("Reminders have been sent");
                    this.$refs.modalActions.hide();
                } else {
                    this.isLoading = false;
                    this._message(
                        "Error: Unable to resend reminders",
                        "danger"
                    );
                }
            }
            // log call
            else if (this.type === LOGCALL) {
                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code === 200
                ) {
                    this._message("Communication record(s) have been created.");
                    this.$refs.modalActions.hide();
                } else {
                    this.isLoading = false;
                    this._message("Unable to create log", "danger");
                }
            }
        }
    }
};
</script>

<style scoped>
.remaining-chars {
    color: grey;
    margin-top: 5px;
    font-size: 10px;
}
</style>
